// Checkout.js
import React, { useState, useEffect } from "react";
import { Modal, View, Text, TextInput, Pressable, Image, StyleSheet, ScrollView, Platform, Alert } from "react-native";
import { Ionicons } from '@expo/vector-icons';
import { useUser } from "../../Widgets/UserContext";
import { getFirestore, doc, updateDoc, collection, query, where, getDocs, arrayUnion } from "firebase/firestore";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import axios from "axios";	



const Checkout = ({ isVisible, onClose, selectedECard, pageOwner }) => {
	const [amount, setAmount] = useState("");
	const processingFee = 5.0;
	const recipientEmail = pageOwner.email;
	const recipientName = pageOwner.firstName;
	const [guestEmail, setGuestEmail] = useState("");
	const [guestName, setGuestName] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isTestMode, setIsTestMode] = useState(false); // Admin test/live toggle

	const stripe = useStripe();
	const elements = useElements();

	let LOCAL_SERVER_URL = "http://localhost:5001/my-tassel/us-central1/api";
	let FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";
	// Always use production Firebase Functions (no emulators)
	const SERVER_URL = FIREBASE_SERVER_URL;

	// Move the console.log into a useEffect that runs once
	useEffect(() => {
		
	}, []); // Empty dependency array means this only runs once when component mounts

	const { currentUser, currentUserData } = useUser();

	// 
	// 
	// 

	// Add this function to format the amount display
	const getFormattedAmount = () => {
		const numAmount = parseFloat(amount);
		return !isNaN(numAmount) ? `$${numAmount.toFixed(2)}` : "$0.00";
	};

	const [showThankYouModal, setShowThankYouModal] = useState(false);

	// Stripe Elements styling
	const stripeElementsStyle = {
		style: {
			base: {
				fontSize: '16px',
				color: '#424770',
				fontFamily: 'Orpheus Pro, sans-serif',
				'::placeholder': {
					color: '#aab7c4',
				},
				backgroundColor: '#fff',
				padding: '10px',
			},
			invalid: {
				color: '#9e2146',
			},
		},
	};

	// Add this function to display user-friendly error messages
	const getErrorMessage = (error) => {
		
		
		// Check for specific error messages from the server
		if (error.message && error.message.includes("transfers capability")) {
			return "The recipient hasn't completed their payment setup. Please try again later or contact them to complete their Stripe account setup.";
		}
		
		// Common Stripe errors with user-friendly messages
		if (error.code === "card_declined") {
			return "Your card was declined. Please try a different payment method.";
		}
		
		if (error.code === "expired_card") {
			return "Your card has expired. Please try a different card.";
		}
		
		if (error.code === "incomplete_number" || error.code === "invalid_number") {
			return "Your card number is invalid. Please check and try again.";
		}
		
		// Default error message
		return "There was a problem processing your payment. Please try again or use a different payment method.";
	};

	// Get Firestore instance
	const db = getFirestore();

	const handleSubmit = async () => {
		setErrorMessage(""); // Clear any previous errors
		const contributionAmount = parseFloat(amount) || 0;
		if (contributionAmount <= 0) {
			setErrorMessage("Please enter a valid contribution amount greater than $0.");
			return;
		}

		if (!pageOwner?.stripeAccountId) {
			setErrorMessage("This page is not yet configured to accept contributions. Please contact the page owner.");
			return;
		}

		setIsProcessing(true); // Start loading indicator

		try {
			// Get card element
			const cardElement = elements.getElement(CardElement);
			if (!cardElement) {
				throw new Error('Card element not found');
			}

			// Create payment method
			const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
				type: 'card',
				card: cardElement,
			});

			if (createError) {
				throw createError;
			}

			// Process the payment with the created payment method
			const { clientSecret } = await processPayment(contributionAmount + processingFee);
			
			// Confirm the payment
			const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
				clientSecret,
				{
					payment_method: paymentMethod.id,
				}
			);

			if (confirmError) {
				throw confirmError;
			}

			if (paymentIntent.status === 'succeeded') {
				// Update the GoalTracker's amountContributed value
				if (db && pageOwner?.uid) {
					try {
						// Get the current goal data
						const usersRef = collection(db, "users");
						const q = query(usersRef, where("uid", "==", pageOwner.uid));
						const querySnapshot = await getDocs(q);
						
						if (!querySnapshot.empty) {
							const userDoc = querySnapshot.docs[0];
							
							// Get current goal data
							const userData = userDoc.data();
							const goalTracker = userData?.pages?.registryPageConfig?.goalTracker || {};
							const currentAmount = parseFloat(goalTracker.amountContributed || 0);
							const updatedAmount = currentAmount + contributionAmount;
					
							// Update the amount contributed
							await updateDoc(doc(db, "users", userDoc.id), {
								"pages.registryPageConfig.goalTracker.amountContributed": updatedAmount,
								"pages.registryPageConfig.goalTracker.contributions": arrayUnion({
									amount: contributionAmount,
									contributorName: currentUserData?.firstName 
										? `${currentUserData.firstName} ${currentUserData.lastName || ''}`
										: guestName,
									contributorId: currentUserData?.uid || "guest",
									timestamp: new Date()
								})
							});
					
							console.log("Updated goal tracker amount:", {
								previousAmount: currentAmount,
								contributionAmount,
								newTotal: updatedAmount
							});

							// If the goal was just reached or exceeded, send the goal-reached email using admin template
							try {
								const targetAmount = parseFloat(goalTracker.targetAmount || 0);
								const wasGoalReached = currentAmount < targetAmount && updatedAmount >= targetAmount;
								const hasGoalReachedEmailBeenSent = goalTracker.goalReachedEmailSent || false;
								if (wasGoalReached && !hasGoalReachedEmailBeenSent && targetAmount > 0) {
									console.log(`Goal reached for ${recipientName}, sending goal-reached email`);
									const contributorCount = (userData?.pages?.registryPageConfig?.contributions || []).length + 1;
									const emailResponse = await fetch(`${SERVER_URL}/send-goal-reached-email`, {
										method: "POST",
										headers: { "Content-Type": "application/json" },
										body: JSON.stringify({
											graduateName: `${pageOwner.firstName} ${pageOwner.lastName || ''}`.trim(),
											graduateEmail: pageOwner.email,
											goalAmount: targetAmount.toFixed(2),
											totalAmount: updatedAmount.toFixed(2),
											contributorCount
										})
									});
									let emailResult = null;
									try { emailResult = await emailResponse.json(); } catch (e) { }
									if (emailResponse.ok && emailResult && emailResult.success) {
										await updateDoc(doc(db, "users", userDoc.id), {
											"pages.registryPageConfig.goalTracker.goalReachedEmailSent": true,
											"pages.registryPageConfig.goalTracker.goalReachedDate": new Date()
										});
										console.log('✅ Goal reached email sent successfully');
									} else {
										console.error('❌ Failed to send goal reached email:', emailResponse.status, emailResult);
										await updateDoc(doc(db, "users", userDoc.id), {
											"pages.registryPageConfig.goalTracker.goalReachedEmailFailed": true,
											"pages.registryPageConfig.goalTracker.goalReachedEmailFailedDate": new Date()
										});
									}
								}
							} catch (emailError) {
								console.error('Error sending goal reached email:', emailError);
							}
						}
					} catch (dbError) {
						
						// Continue with the flow even if the DB update fails
					}
				}
				
				// Send the eCard after successful payment (but don't let it block the success modal)
				try {
					await sendECard({
						frontImageUrl: selectedECard?.frontImageUrl,
						backImageUrl: selectedECard?.backImageUrl,
						contributionAmount: amount,
						recipientEmail: recipientEmail,
						senderEmail: currentUserData?.email || currentUser?.email || guestEmail,
						senderName: currentUserData?.firstName || guestName,
						name: recipientName
					});
					
				} catch (emailError) {
					
					// Don't throw error - payment was successful, just email failed
				}
				
				// Always show success modal after payment succeeds
				setShowThankYouModal(true);
			}
		} catch (error) {
			
			setErrorMessage(getErrorMessage(error));
		} finally {
			setIsProcessing(false); // Stop loading indicator
		}
	};

	// Update processPayment to return the clientSecret
	const processPayment = async (totalAmount) => {
		try {
			// Verify pageOwner and stripeAccountId
			if (!pageOwner || !pageOwner.stripeAccountId) {
				throw new Error("Stripe account not properly configured. Please contact the page owner.");
			}

			
			
			
			const response = await axios.post(`${SERVER_URL}/create-payment-intent`, {
				amount: Math.round(totalAmount * 100), // Convert to cents and ensure it's an integer
				currency: "usd",
				sellerAccountId: pageOwner.stripeAccountId,
				isTestMode: isTestMode,
				isAdmin: currentUserData?.accountType === 'admin',
			});

			if (!response.data || !response.data.clientSecret) {
				throw new Error("Invalid response from payment server");
			}

			return { clientSecret: response.data.clientSecret };
		} catch (error) {
			console.error("Payment processing error:", {
				error: error.message,
				pageOwnerDetails: {
					hasStripeAccount: !!pageOwner?.stripeAccountId,
					email: pageOwner?.email
				}
			});
			
			// Check if the error response contains our structured error data
			if (error.response && error.response.data) {
				const errorData = error.response.data;
				
				// If we have a structured error message from our server, use it
				if (errorData.message) {
					throw new Error(errorData.message);
				}
			}
			
			// Default error handling
			throw new Error(error.message || "Failed to process payment. Please try again.");
		}
	};

	const sendECard = async (eCardData) => {
		try {
			const response = await fetch(`${SERVER_URL}/send-ecard-email`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(eCardData),
				});

			if (!response.ok) throw new Error("Failed to send eCard");
		} catch (error) {
			
			throw error; // Rethrow to handle in the calling function
		}
	};

	const ThankYouModal = () => (
		<Modal visible={showThankYouModal} animationType="fade" transparent={true}>
			<View style={styles.centeredView}>
				<View style={styles.thankYouModalView}>
					<Image 
						source={require('../../assets/logo.png')} 
						style={styles.thankYouLogo}
					/>
					<Text style={styles.thankYouTitle}>Thank You!</Text>
					<Text style={styles.thankYouText}>
						Your contribution of {getFormattedAmount()} has been successfully processed. 
						{recipientEmail && "An eCard confirmation has been sent to your email."}
					</Text>
					<Pressable
						style={styles.thankYouButton}
						onPress={() => {
							setShowThankYouModal(false);
							onClose();
						}}
					>
						<Text style={styles.thankYouButtonText}>Close</Text>
					</Pressable>
				</View>
			</View>
		</Modal>
	);

	// Memoize the GuestInfoForm component to prevent unnecessary re-renders
	const GuestInfoForm = React.useMemo(() => {
		if (currentUserData) return null;

		return (
			<View style={styles.guestInfoContainer}>
				<Text style={styles.sectionTitle}>Guest Information</Text>
				<View style={styles.inputContainer}>
					<Text style={styles.inputLabel}>Your Name *</Text>
					<TextInput
						style={styles.input}
						placeholder="Enter your name"
						value={guestName}
						onChangeText={(text) => setGuestName(text)}
						autoComplete="name"
						blurOnSubmit={false}
						placeholderTextColor="#999"
					/>
				</View>
				<View style={styles.inputContainer}>
					<Text style={styles.inputLabel}>Your Email *</Text>
					<TextInput
						style={styles.input}
						placeholder="Enter your email"
						value={guestEmail}
						onChangeText={(text) => setGuestEmail(text)}
						keyboardType="email-address"
						autoComplete="email"
						blurOnSubmit={false}
						placeholderTextColor="#999"
					/>
				</View>
			</View>
		);
	}, [currentUserData, guestName, guestEmail]); // Dependencies for useMemo

	// Admin test/live mode toggle component
	const AdminModeToggle = React.useMemo(() => {
		if (currentUserData?.accountType !== 'admin') return null;

		return (
			<View style={styles.adminToggleContainer}>
				<Text style={styles.adminToggleLabel}>🔑 Admin Payment Mode:</Text>
				<View style={styles.toggleButtons}>
					<Pressable
						style={[
							styles.toggleButton,
							!isTestMode && styles.toggleButtonActive
						]}
						onPress={() => setIsTestMode(false)}
					>
						<Text style={[
							styles.toggleButtonText,
							!isTestMode && styles.toggleButtonTextActive
						]}>
							💳 LIVE
						</Text>
					</Pressable>
					<Pressable
						style={[
							styles.toggleButton,
							isTestMode && styles.toggleButtonActive
						]}
						onPress={() => setIsTestMode(true)}
					>
						<Text style={[
							styles.toggleButtonText,
							isTestMode && styles.toggleButtonTextActive
						]}>
							🧪 TEST
						</Text>
					</Pressable>
				</View>
				<Text style={styles.adminToggleHint}>
					{isTestMode ? 'Using TEST Stripe keys' : 'Using LIVE Stripe keys'}
				</Text>
			</View>
		);
	}, [currentUserData, isTestMode]);

	return (
		<Modal 
			visible={isVisible} 
			animationType="slide" 
			transparent={true}
			onRequestClose={onClose}
		>
			<View style={styles.modalOverlay}>
				<View style={styles.modalView}>
					<View style={styles.headerContainer}>
						<Text style={styles.modalTitle}>Complete Your Contribution</Text>
						<Pressable style={styles.closeIconButton} onPress={onClose}>
							<Ionicons name="close" size={24} color="#666" />
						</Pressable>
					</View>

					<ScrollView 
						style={styles.scrollableContent}
						contentContainerStyle={styles.scrollableContentContainer}
						showsVerticalScrollIndicator={true}
					>
						{selectedECard &&
							<View style={styles.eCardContainer}>
								<Image source={{ uri: selectedECard.frontImageUrl }} style={styles.eCardPreview} />
								<Image source={{ uri: selectedECard.backImageUrl }} style={styles.eCardPreview} />
							</View>
						}
						{AdminModeToggle}
						{GuestInfoForm}
						<View style={styles.inputContainer}>
							<Text style={styles.inputLabel}>Contribution Amount *</Text>
							<TextInput 
								style={styles.input} 
								placeholder="Enter contribution amount" 
								value={amount} 
								onChangeText={setAmount} 
								keyboardType="numeric"
								placeholderTextColor="#999" 
							/>
						</View>
						<View style={styles.summary}>
							<Text style={styles.summaryText}>Contribution: {getFormattedAmount()}</Text>
							<Text style={styles.summaryText}>Processing Fee: $5.00</Text>
							<Text style={styles.summaryTextTotal}>
								Total: ${((parseFloat(amount) || 0) + processingFee).toFixed(2)}
							</Text>
						</View>
						<View style={styles.cardInputContainer}>
							<Text style={styles.sectionTitle}>Payment Information</Text>
							<View style={styles.stripeElementWrapper}>
								<CardElement options={stripeElementsStyle} />
							</View>
						</View>
						
						{errorMessage ? (
							<View style={styles.errorContainer}>
								<Text style={styles.errorText}>{errorMessage}</Text>
							</View>
						) : null}
						

						<View style={styles.buttonContainer}>
							<Pressable 
								style={isProcessing ? [styles.primaryButton, styles.disabledButton] : styles.primaryButton} 
								onPress={handleSubmit}
								disabled={isProcessing}
							>
								{isProcessing ? (
									<View style={styles.loadingContainer}>
										<Text style={styles.primaryButtonText}>Processing...</Text>
									</View>
								) : (
									<>
										<Ionicons name="card-outline" size={20} color="white" style={styles.buttonIcon} />
										<Text style={styles.primaryButtonText}>Complete Contribution</Text>
									</>
								)}
							</Pressable>
						</View>
					</ScrollView>
				</View>
			</View>
			<ThankYouModal />
		</Modal>
	);
};

const styles = StyleSheet.create({
	modalOverlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'flex-start',
		alignItems: 'center',
		paddingTop: 20,
	},
	modalView: {
		width: '95%',
		maxWidth: 800,
		height: '90%',
		backgroundColor: '#fefde2',
		borderRadius: 24,
		padding: 0,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 10 },
		shadowOpacity: 0.3,
		shadowRadius: 20,
		elevation: 10,
	},
	headerContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#e6d690',
		backgroundColor: '#fefde2',
		borderTopLeftRadius: 24,
		borderTopRightRadius: 24,
	},
	modalTitle: {
		fontSize: 24,
		fontWeight: '700',
		color: '#2d3748',
		fontFamily: Platform.OS === 'ios' ? 'System' : 'Roboto',
	},
	closeIconButton: {
		padding: 8,
		borderRadius: 20,
		backgroundColor: '#f0f0f0',
	},
	scrollableContent: {
		flex: 1,
	},
	scrollableContentContainer: {
		padding: 24,
		paddingTop: 0,
		paddingBottom: 40,
	},
	eCardContainer: {
		flexDirection: 'row',
		justifyContent: 'center',
		flexWrap: 'wrap',
		gap: 16,
		marginBottom: 24,
		padding: 16,
		backgroundColor: '#f5f3e7',
		borderRadius: 16,
		borderWidth: 1,
		borderColor: '#d4c5a9',
	},
	eCardPreview: {
		width: 240,
		height: 320,
		resizeMode: 'contain',
		borderRadius: 12,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.15,
		shadowRadius: 12,
	},
	inputContainer: {
		marginBottom: 20,
	},
	inputLabel: {
		fontSize: 16,
		fontWeight: '600',
		color: '#2d3748',
		marginBottom: 8,
	},
	input: {
		width: '100%',
		padding: 16,
		borderWidth: 2,
		borderColor: '#d4c5a9',
		borderRadius: 12,
		fontSize: 16,
		color: '#2d3748',
		backgroundColor: '#ffffff',
	},
	sectionTitle: {
		fontSize: 18,
		fontWeight: '700',
		color: '#2d3748',
		marginBottom: 16,
	},
	summary: {
		marginBottom: 24,
		width: '100%',
		padding: 16,
		backgroundColor: '#f5f3e7',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#d4c5a9',
	},
	summaryText: {
		fontSize: 16,
		lineHeight: 24,
		color: '#2d3748',
		marginBottom: 4,
	},
	summaryTextTotal: {
		fontSize: 18,
		fontWeight: '700',
		color: '#4c956c',
		marginTop: 8,
		borderTopWidth: 1,
		borderTopColor: '#d4c5a9',
		paddingTop: 8,
	},
	cardInputContainer: {
		width: '100%',
		marginBottom: 20,
	},
	stripeElementWrapper: {
		backgroundColor: '#ffffff',
		borderRadius: 12,
		padding: 16,
		borderWidth: 2,
		borderColor: '#d4c5a9',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 8,
		elevation: 3,
	},
	buttonContainer: {
		width: '100%',
		alignItems: 'center',
		marginTop: 8,
	},
	primaryButton: {
		flexDirection: 'row',
		backgroundColor: '#4c956c',
		paddingVertical: 16,
		paddingHorizontal: 32,
		borderRadius: 12,
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 250,
		shadowColor: '#4c956c',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 4,
	},
	disabledButton: {
		backgroundColor: '#a0aec0',
		shadowColor: '#a0aec0',
	},
	buttonIcon: {
		marginRight: 8,
	},
	primaryButtonText: {
		color: '#ffffff',
		fontSize: 18,
		fontWeight: '700',
	},
	centeredView: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	thankYouModalView: {
		backgroundColor: '#fefde2',
		borderRadius: 24,
		padding: 32,
		alignItems: 'center',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 10 },
		shadowOpacity: 0.3,
		shadowRadius: 20,
		elevation: 10,
		width: '90%',
		maxWidth: 500,
	},
	thankYouLogo: {
		width: 100,
		height: 100,
		marginBottom: 20,
	},
	thankYouTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#4c956c',
		marginBottom: 15,
		fontFamily: 'Orpheus Pro',
	},
	thankYouText: {
		fontSize: 16,
		textAlign: 'center',
		marginBottom: 20,
		lineHeight: 24,
		color: '#424770',
		fontFamily: 'Orpheus Pro',
	},
	thankYouButton: {
		flexDirection: 'row',
		backgroundColor: '#4c956c',
		borderRadius: 12,
		paddingVertical: 16,
		paddingHorizontal: 32,
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 200,
		shadowColor: '#4c956c',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 4,
	},
	thankYouButtonText: {
		color: '#ffffff',
		fontWeight: '700',
		textAlign: 'center',
		fontSize: 16,
	},

	guestInfoContainer: {
		width: '100%',
		marginBottom: 24,
		padding: 20,
		backgroundColor: '#f5f3e7',
		borderRadius: 16,
		borderWidth: 1,
		borderColor: '#d4c5a9',
	},
	
	errorContainer: {
		backgroundColor: '#fef2f2',
		borderColor: '#fee2e2',
		borderWidth: 2,
		borderRadius: 12,
		padding: 16,
		marginBottom: 20,
		width: '100%',
	},
	errorText: {
		color: '#ef4444',
		fontSize: 16,
		lineHeight: 22,
		textAlign: 'center',
		fontWeight: '600',
	},
	
	loadingContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},

	// Admin toggle styles
	adminToggleContainer: {
		width: '100%',
		marginBottom: 24,
		padding: 20,
		backgroundColor: '#fff9e6',
		borderRadius: 16,
		borderWidth: 2,
		borderColor: '#ffd700',
	},
	adminToggleLabel: {
		fontSize: 16,
		fontWeight: '700',
		color: '#2d3748',
		marginBottom: 12,
		textAlign: 'center',
	},
	toggleButtons: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: 12,
	},
	toggleButton: {
		flex: 1,
		padding: 14,
		borderRadius: 12,
		backgroundColor: '#f5f5f5',
		borderWidth: 2,
		borderColor: '#d4c5a9',
		alignItems: 'center',
	},
	toggleButtonActive: {
		backgroundColor: '#4c956c',
		borderColor: '#4c956c',
	},
	toggleButtonText: {
		fontSize: 14,
		fontWeight: '600',
		color: '#666',
	},
	toggleButtonTextActive: {
		color: '#fff',
	},
	adminToggleHint: {
		fontSize: 12,
		color: '#666',
		textAlign: 'center',
		marginTop: 8,
		fontStyle: 'italic',
	},
});

// Add a keyframe animation for the dots
if (typeof document !== 'undefined') {
	const style = document.createElement('style');
	style.textContent = `
		@keyframes pulse {
			0%, 80%, 100% { 
				transform: scale(0.6);
				opacity: 0.4;
			}
			40% { 
				transform: scale(1);
				opacity: 1;
			}
		}
	`;
	document.head.appendChild(style);
}

export default Checkout;